const ROLLOVER_GRACE_MS = 25;
export const DAILY_REFRESH_RETRY_DELAYS_MS = [15_000, 30_000, 60_000] as const;

type DailyRefreshResult = boolean | void;
type DailyRefresh = () => DailyRefreshResult | Promise<DailyRefreshResult>;

export function millisecondsUntilNextUtcDay(now = Date.now()): number {
  const date = new Date(now);
  const next = Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate() + 1,
  ) + ROLLOVER_GRACE_MS;
  return Math.max(1, next - now);
}

/**
 * Keeps a long-lived installed PWA on the server's current UTC Daily without
 * polling. The callback owns networking and UI refresh; this class owns only
 * lifecycle signals and the rollover clock.
 */
export class DailyChallengeRefreshScheduler {
  private rolloverTimer: ReturnType<typeof globalThis.setTimeout> | undefined;
  private retryTimer: ReturnType<typeof globalThis.setTimeout> | undefined;
  private refreshInFlight: Promise<DailyRefreshResult> | undefined;
  private refreshQueued = false;
  private retryAttempt = 0;
  private started = false;
  private lifecycleGeneration = 0;

  constructor(private readonly refresh: DailyRefresh) {}

  start(): void {
    if (this.started) return;
    this.started = true;
    this.lifecycleGeneration += 1;
    window.addEventListener('online', this.onOnline);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.scheduleRollover();
  }

  stop(): void {
    if (!this.started) return;
    this.started = false;
    this.lifecycleGeneration += 1;
    window.removeEventListener('online', this.onOnline);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    if (this.rolloverTimer !== undefined) globalThis.clearTimeout(this.rolloverTimer);
    if (this.retryTimer !== undefined) globalThis.clearTimeout(this.retryTimer);
    this.rolloverTimer = undefined;
    this.retryTimer = undefined;
    this.refreshInFlight = undefined;
    this.refreshQueued = false;
    this.retryAttempt = 0;
  }

  private scheduleRollover(): void {
    if (!this.started) return;
    if (this.rolloverTimer !== undefined) globalThis.clearTimeout(this.rolloverTimer);
    this.rolloverTimer = globalThis.setTimeout(() => {
      this.rolloverTimer = undefined;
      this.requestRefresh();
      this.scheduleRollover();
    }, millisecondsUntilNextUtcDay());
  }

  private requestRefresh(): void {
    if (!this.started) return;
    if (this.refreshInFlight) {
      this.refreshQueued = true;
      return;
    }
    const generation = this.lifecycleGeneration;
    const refresh = Promise.resolve().then(() => this.refresh());
    this.refreshInFlight = refresh;
    void refresh.then(
      (result) => {
        if (!this.started || generation !== this.lifecycleGeneration) return;
        if (result === false) this.scheduleRetry();
        else this.resetRetry();
      },
      () => {
        if (this.started && generation === this.lifecycleGeneration) this.scheduleRetry();
      },
    ).finally(() => {
      if (this.refreshInFlight === refresh) this.refreshInFlight = undefined;
      if (
        !this.started
        || generation !== this.lifecycleGeneration
        || !this.refreshQueued
      ) return;
      this.refreshQueued = false;
      this.resetRetry();
      this.requestRefresh();
    });
  }

  private scheduleRetry(): void {
    if (!this.started || this.retryTimer !== undefined) return;
    const delay = DAILY_REFRESH_RETRY_DELAYS_MS[
      Math.min(this.retryAttempt, DAILY_REFRESH_RETRY_DELAYS_MS.length - 1)
    ]!;
    this.retryAttempt += 1;
    this.retryTimer = globalThis.setTimeout(() => {
      this.retryTimer = undefined;
      this.requestRefresh();
    }, delay);
  }

  private resetRetry(): void {
    if (this.retryTimer !== undefined) globalThis.clearTimeout(this.retryTimer);
    this.retryTimer = undefined;
    this.retryAttempt = 0;
  }

  private readonly onOnline = (): void => {
    this.resetRetry();
    this.requestRefresh();
    this.scheduleRollover();
  };

  private readonly onVisibilityChange = (): void => {
    if (document.hidden) return;
    this.resetRetry();
    this.requestRefresh();
    this.scheduleRollover();
  };
}
